import { ref, onMounted, onUnmounted, type Ref } from 'vue';

interface ArrivedState {
  top: boolean;
  right: boolean;
  bottom: boolean;
  left: boolean;
}

interface UseScrollPositionOptions {
  offset?: number;
  throttle?: number;
}

export function useScrollPosition(
  target?: Ref<HTMLElement | null>,
  options: UseScrollPositionOptions = {}
) {
  const { offset = 0, throttle = 0 } = options;

  const x = ref(0);
  const y = ref(0);
  const isScrolling = ref(false);

  const arrivedState = ref<ArrivedState>({
    top: true,
    right: false,
    bottom: false,
    left: true,
  });

  let scrollTimeout: ReturnType<typeof setTimeout> | null = null;
  let throttleTimeout: ReturnType<typeof setTimeout> | null = null;

  const updateScroll = () => {
    const element = target?.value;

    if (element) {
      x.value = element.scrollLeft;
      y.value = element.scrollTop;

      const { scrollWidth, scrollHeight, clientWidth, clientHeight } = element;

      arrivedState.value = {
        top: y.value <= offset,
        right: x.value + clientWidth >= scrollWidth - offset,
        bottom: y.value + clientHeight >= scrollHeight - offset,
        left: x.value <= offset,
      };
    } else {
      x.value = window.scrollX;
      y.value = window.scrollY;

      const scrollWidth = document.documentElement.scrollWidth;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientWidth = window.innerWidth;
      const clientHeight = window.innerHeight;

      arrivedState.value = {
        top: y.value <= offset,
        right: x.value + clientWidth >= scrollWidth - offset,
        bottom: y.value + clientHeight >= scrollHeight - offset,
        left: x.value <= offset,
      };
    }

    isScrolling.value = true;

    if (scrollTimeout) {
      clearTimeout(scrollTimeout);
    }

    scrollTimeout = setTimeout(() => {
      isScrolling.value = false;
    }, 150);
  };

  const throttledUpdateScroll = () => {
    if (throttle > 0) {
      if (!throttleTimeout) {
        updateScroll();
        throttleTimeout = setTimeout(() => {
          throttleTimeout = null;
        }, throttle);
      }
    } else {
      updateScroll();
    }
  };

  onMounted(() => {
    const element = target?.value;

    if (element) {
      element.addEventListener('scroll', throttledUpdateScroll, { passive: true });
      updateScroll();
    } else {
      window.addEventListener('scroll', throttledUpdateScroll, { passive: true });
      updateScroll();
    }
  });

  onUnmounted(() => {
    const element = target?.value;

    if (scrollTimeout) {
      clearTimeout(scrollTimeout);
    }

    if (throttleTimeout) {
      clearTimeout(throttleTimeout);
    }

    if (element) {
      element.removeEventListener('scroll', throttledUpdateScroll);
    } else {
      window.removeEventListener('scroll', throttledUpdateScroll);
    }
  });

  return { x, y, arrivedState, isScrolling };
}
